import React, { useEffect } from "react";
import { ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * FAQ Component with Schema.org structured data for Google Rich Snippets
 * 
 * Usage:
 * <FAQSection 
 *   faqs={[{ question: "...", answer: "..." }]} 
 *   title="Questions fréquentes"
 *   lang="fr"
 * />
 */

// FAQ Data by category and language
export const faqData = {
  trek: {
    fr: [
      {
        question: "Quelle est la meilleure période pour un trek dans le désert marocain ?",
        answer: "La meilleure période pour un trek désert Maroc s'étend de mi-septembre à mi-mai. Les températures sont agréables (15-25°C en journée). Évitez l'été (juin-août) où les températures peuvent dépasser 45°C. L'automne (octobre-novembre) et le printemps (mars-avril) sont idéaux pour la randonnée dans le Sahara."
      },
      {
        question: "Quel niveau physique faut-il pour un trek dans le Sahara ?",
        answer: "Nos treks désert Maroc sont accessibles à tous. Pas besoin d'être sportif ! Nous marchons 4-6 heures par jour à un rythme tranquille (3-4 km/h). Les dromadaires portent les bagages. Pour les treks de 5-7 jours, une bonne condition physique générale est recommandée. Nous adaptons toujours le rythme au groupe."
      },
      {
        question: "Que faut-il emporter pour un trek désert Maroc ?",
        answer: "Pour votre randonnée Sahara, prévoyez : vêtements chauds pour les nuits (0-10°C), vêtements légers pour le jour, chaussures de marche confortables, chapeau/chèche, lunettes de soleil, crème solaire SPF50, sac de couchage (ou location sur place), lampe frontale, et gourde. Nous fournissons la liste complète à la réservation."
      },
      {
        question: "Comment se passe une nuit en bivouac dans le désert ?",
        answer: "Le bivouac est une expérience magique ! Nous installons le camp au pied des dunes d'Erg Chegaga ou Erg Zahar. Vous dormez sous tente berbère traditionnelle ou à la belle étoile. Le dîner est préparé sur feu de bois par notre cuisinier. C'est l'occasion d'observer les étoiles du Sahara, loin de toute pollution lumineuse."
      },
      {
        question: "Les treks sont-ils adaptés aux familles avec enfants ?",
        answer: "Oui ! Nous proposons des treks famille adaptés aux enfants dès 6 ans. Le rythme est plus doux, avec des étapes courtes et des activités ludiques (balade à dromadaire, jeux dans les dunes). Les enfants adorent l'expérience du bivouac et le contact avec les nomades. Contactez-nous pour un trek sur mesure."
      },
      {
        question: "Quelle est la différence entre Erg Chegaga et Erg Chebbi ?",
        answer: "Erg Chegaga (où nous opérons) est plus sauvage, moins touristique, avec les plus hautes dunes du Maroc (jusqu'à 300m). Accessible uniquement en 4x4 ou à pied, il offre une expérience authentique. Erg Chebbi (Merzouga) est plus accessible mais plus fréquenté. Pour un trek désert authentique hors des sentiers battus, Erg Chegaga est le meilleur choix."
      }
    ],
    en: [
      {
        question: "What is the best time for a desert trek in Morocco?",
        answer: "The best time for a Morocco desert trek is from mid-September to mid-May. Temperatures are pleasant (15-25°C during the day). Avoid summer (June-August) when temperatures can exceed 45°C. Autumn (October-November) and spring (March-April) are ideal for Sahara hiking."
      },
      {
        question: "What fitness level is required for a Sahara trek?",
        answer: "Our Morocco desert treks are accessible to everyone. No need to be athletic! We walk 4-6 hours per day at a leisurely pace (3-4 km/h). Camels carry the luggage. For 5-7 day treks, good general fitness is recommended. We always adapt the pace to the group."
      },
      {
        question: "What should I pack for a Morocco desert trek?",
        answer: "For your Sahara hike, bring: warm clothes for nights (0-10°C), light clothes for day, comfortable hiking shoes, hat/cheche, sunglasses, SPF50 sunscreen, sleeping bag (or rent on site), headlamp, and water bottle. We provide the complete list upon booking."
      },
      {
        question: "What is a desert bivouac like?",
        answer: "Bivouac camping is a magical experience! We set up camp at the foot of Erg Chegaga or Erg Zahar dunes. You sleep in traditional Berber tents or under the stars. Dinner is cooked over wood fire by our chef. It's an opportunity to observe the Sahara stars, far from any light pollution."
      },
      {
        question: "Are treks suitable for families with children?",
        answer: "Yes! We offer family treks adapted for children from 6 years old. The pace is gentler, with short stages and fun activities (camel rides, dune games). Children love the bivouac experience and contact with nomads. Contact us for a custom family trek."
      },
      {
        question: "What is the difference between Erg Chegaga and Erg Chebbi?",
        answer: "Erg Chegaga (where we operate) is wilder, less touristy, with Morocco's highest dunes (up to 300m). Only accessible by 4x4 or on foot, it offers an authentic experience. Erg Chebbi (Merzouga) is more accessible but more crowded. For an authentic off-the-beaten-path desert trek, Erg Chegaga is the best choice."
      }
    ]
  },
  
  circuits4x4: {
    fr: [
      {
        question: "Quelle est la durée idéale pour un circuit 4x4 dans le désert marocain ?",
        answer: "La durée idéale dépend de votre point de départ. Depuis Marrakech, nous recommandons minimum 3 jours (2 nuits) pour atteindre Erg Chegaga. Pour une expérience complète avec la vallée du Draa, Ouarzazate et les kasbahs, optez pour 5-7 jours. Les circuits courts de 2 jours sont possibles depuis Ouarzazate ou Zagora."
      },
      {
        question: "Les circuits 4x4 sont-ils confortables ?",
        answer: "Nos circuits 4x4 Sahara utilisent des Toyota Land Cruiser climatisés, véhicules fiables et confortables adaptés aux pistes du désert. Les chauffeurs-guides sont expérimentés. Nous faisons des pauses régulières. L'hébergement varie selon la formule : bivouac de luxe, camp nomade authentique ou hôtel. Tout est personnalisable."
      },
      {
        question: "Peut-on combiner circuit 4x4 et trek à pied ?",
        answer: "Absolument ! C'est même notre spécialité. Beaucoup de nos clients font un circuit 4x4 pour rejoindre le désert profond depuis Marrakech, puis enchaînent avec 2-3 jours de trek à pied dans les dunes d'Erg Chegaga. Retour en 4x4. C'est la formule idéale pour découvrir le Sahara sous tous ses aspects."
      },
      {
        question: "Quels sites visite-t-on lors d'un circuit 4x4 désert Maroc ?",
        answer: "Nos circuits 4x4 incluent : le col du Tizi n'Tichka (2260m), Aït Ben Haddou (UNESCO), Ouarzazate et ses studios, la vallée du Draa et ses palmeraies, Zagora (52 jours vers Tombouctou), M'Hamid El Ghizlane (porte du désert), les dunes d'Erg Chegaga ou Erg Zahar, le lac Iriki. Itinéraires personnalisables selon vos envies."
      },
      {
        question: "Est-il possible de faire un circuit 4x4 privé ?",
        answer: "Oui, tous nos circuits 4x4 peuvent être en privé (juste vous et votre chauffeur-guide) ou en petit groupe (max 6-8 personnes). Le privé offre plus de flexibilité : vous choisissez les dates, le rythme, les arrêts. C'est idéal pour les familles, couples ou groupes d'amis. Nous proposons aussi des circuits sur mesure 100% personnalisés."
      },
      {
        question: "Quel est le prix d'un circuit 4x4 dans le désert marocain ?",
        answer: "Nos circuits 4x4 Sahara démarrent à partir de 150€/personne/jour en groupe, incluant : véhicule 4x4 climatisé, chauffeur-guide, hébergement (bivouac ou hôtel), repas complets, et activités. Le prix varie selon la durée, le type d'hébergement (standard ou luxe) et si le circuit est privé ou en groupe. Devis gratuit sur demande."
      }
    ],
    en: [
      {
        question: "What is the ideal duration for a 4x4 desert tour in Morocco?",
        answer: "The ideal duration depends on your starting point. From Marrakech, we recommend minimum 3 days (2 nights) to reach Erg Chegaga. For a complete experience with Draa valley, Ouarzazate and kasbahs, opt for 5-7 days. Short 2-day tours are possible from Ouarzazate or Zagora."
      },
      {
        question: "Are the 4x4 tours comfortable?",
        answer: "Our Sahara 4x4 tours use air-conditioned Toyota Land Cruisers, reliable and comfortable vehicles adapted to desert tracks. Driver-guides are experienced. We make regular stops. Accommodation varies: luxury bivouac, authentic nomad camp or hotel. Everything is customizable."
      },
      {
        question: "Can you combine a 4x4 tour with hiking?",
        answer: "Absolutely! It's our specialty. Many clients do a 4x4 tour to reach the deep desert from Marrakech, then continue with 2-3 days of hiking in the Erg Chegaga dunes. Return by 4x4. It's the ideal formula to discover the Sahara from all angles."
      },
      {
        question: "What sites do you visit during a Morocco desert 4x4 tour?",
        answer: "Our 4x4 tours include: Tizi n'Tichka pass (2260m), Aït Ben Haddou (UNESCO), Ouarzazate and its studios, Draa valley and palm groves, Zagora (52 days to Timbuktu), M'Hamid El Ghizlane (gateway to desert), Erg Chegaga or Erg Zahar dunes, Lake Iriki. Itineraries customizable to your wishes."
      },
      {
        question: "Is it possible to do a private 4x4 tour?",
        answer: "Yes, all our 4x4 tours can be private (just you and your driver-guide) or small group (max 6-8 people). Private offers more flexibility: you choose dates, pace, stops. Ideal for families, couples or groups of friends. We also offer 100% custom tours."
      },
      {
        question: "What is the price of a 4x4 desert tour in Morocco?",
        answer: "Our Sahara 4x4 tours start from €150/person/day in group, including: air-conditioned 4x4, driver-guide, accommodation (bivouac or hotel), full meals, and activities. Price varies by duration, accommodation type (standard or luxury) and private vs group. Free quote on request."
      }
    ]
  },

  retraites: {
    fr: [
      {
        question: "Qu'est-ce qu'une retraite spirituelle dans le désert ?",
        answer: "Une retraite spirituelle dans le désert marocain est une parenthèse de ressourcement loin du quotidien. Le Sahara, par son immensité et son silence, est le cadre idéal pour la méditation, le yoga, et la reconnexion à soi. Nos retraites durent de 3 à 7 jours et incluent des pratiques guidées, des temps de silence, et l'immersion dans la vie nomade."
      },
      {
        question: "Faut-il avoir de l'expérience en yoga ou méditation ?",
        answer: "Non, aucune expérience préalable n'est requise. Nos retraites yoga désert Maroc et stages méditation Sahara sont ouverts aux débutants comme aux pratiquants confirmés. Les enseignants adaptent les séances à tous les niveaux. L'essentiel est d'être ouvert à l'expérience et au lâcher-prise."
      },
      {
        question: "Comment se déroule une journée type en retraite désert ?",
        answer: "Une journée type : réveil au lever du soleil avec yoga ou méditation sur les dunes, petit-déjeuner berbère, temps libre ou atelier (breathwork, sound healing), déjeuner léger, marche méditative dans le désert, temps de repos/journaling, yoga au coucher du soleil, dîner autour du feu, cercle de partage ou silence. Programme adaptable."
      },
      {
        question: "Quels types de retraites proposez-vous ?",
        answer: "Nous proposons : retraite yoga et méditation, retraite silence (vipassana adapté), retraite bien-être holistique (yoga, breathwork, sound healing), retraite développement personnel, retraite créative (écriture, art), et retraites sur mesure. Chaque retraite peut être combinée avec un trek pour une immersion totale."
      },
      {
        question: "Les retraites sont-elles adaptées aux personnes seules ?",
        answer: "Absolument ! Beaucoup de participants viennent seuls et c'est une expérience enrichissante. Le groupe crée des liens profonds dans ce cadre unique. Si vous préférez une retraite individuelle (solo retreat), nous proposons aussi des formules en privé avec accompagnement personnalisé."
      },
      {
        question: "Quel est le confort lors d'une retraite dans le désert ?",
        answer: "Nous proposons différents niveaux de confort : camp nomade authentique (tentes berbères, toilettes sèches, douche solaire) ou camp de luxe (tentes spacieuses avec lits, salle de bain privée). La nourriture est végétarienne, saine et préparée avec soin. L'essentiel est préservé : le silence, l'espace, et la connexion à la nature."
      }
    ],
    en: [
      {
        question: "What is a spiritual retreat in the desert?",
        answer: "A spiritual retreat in the Moroccan desert is a break for rejuvenation away from daily life. The Sahara, with its vastness and silence, is the ideal setting for meditation, yoga, and self-reconnection. Our retreats last 3 to 7 days and include guided practices, silent times, and immersion in nomadic life."
      },
      {
        question: "Do I need yoga or meditation experience?",
        answer: "No, no prior experience is required. Our desert yoga retreats Morocco and Sahara meditation workshops are open to beginners and experienced practitioners alike. Teachers adapt sessions to all levels. The key is being open to the experience and letting go."
      },
      {
        question: "What does a typical day look like during a desert retreat?",
        answer: "A typical day: wake at sunrise with yoga or meditation on the dunes, Berber breakfast, free time or workshop (breathwork, sound healing), light lunch, meditative walk in the desert, rest/journaling time, sunset yoga, dinner around the fire, sharing circle or silence. Schedule adaptable."
      },
      {
        question: "What types of retreats do you offer?",
        answer: "We offer: yoga and meditation retreat, silence retreat (adapted vipassana), holistic wellness retreat (yoga, breathwork, sound healing), personal development retreat, creative retreat (writing, art), and custom retreats. Each retreat can be combined with a trek for total immersion."
      },
      {
        question: "Are retreats suitable for solo travelers?",
        answer: "Absolutely! Many participants come alone and it's an enriching experience. The group creates deep connections in this unique setting. If you prefer an individual retreat, we also offer private formulas with personalized guidance."
      },
      {
        question: "What is the comfort level during a desert retreat?",
        answer: "We offer different comfort levels: authentic nomad camp (Berber tents, dry toilets, solar shower) or luxury camp (spacious tents with beds, private bathroom). Food is vegetarian, healthy and carefully prepared. The essentials are preserved: silence, space, and connection to nature."
      }
    ]
  }
};

const FAQSection = ({ faqs, title, lang = "fr", category = null }) => {
  // Use provided faqs or get from category
  const faqList = faqs || (category && faqData[category]?.[lang]) || [];
  
  useEffect(() => {
    if (faqList.length === 0) return;
    
    // Generate and inject FAQ Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqList.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
    
    // Remove existing FAQ schema if any
    const existingScript = document.querySelector('script[data-faq-schema]');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Add new FAQ schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-faq-schema', 'true');
    script.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    
    return () => {
      // Cleanup on unmount
      const scriptToRemove = document.querySelector('script[data-faq-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [faqList]);

  if (faqList.length === 0) return null;

  const defaultTitle = {
    fr: "Questions fréquentes",
    en: "Frequently Asked Questions",
    es: "Preguntas frecuentes",
    nl: "Veelgestelde vragen",
    ar: "الأسئلة الشائعة"
  };

  return (
    <section className="py-16 bg-muted/30" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <span className="text-primary font-medium tracking-widest uppercase text-sm">
            FAQ
          </span>
          <h2 className="font-serif text-3xl md:text-4xl font-bold mt-2">
            {title || defaultTitle[lang] || defaultTitle.fr}
          </h2>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-4">
          {faqList.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`faq-${index}`}
              className="bg-card border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
            >
              <AccordionTrigger className="text-left font-semibold hover:no-underline py-5">
                <span className="pr-4">{faq.question}</span>
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

export default FAQSection;
